/**
 * 2026 秋季推文页 —— 小麦高端智能裁床 1820S-8CM（中英双语）
 *
 * 内容全部来自 PDF《小麦智能裁床-秋季推文（中英双语·对外发布版）》，
 * 结构化数据放在同目录 content.json：
 *   - 本组件用 React 渲染给访客看
 *   - scripts/generate-static-html.js 也读同一份数据，把正文注入到静态 HTML，
 *     供不执行 JS 的 AI 爬虫（GPTBot / Bytespider 等）抓取
 *
 * 页面本身不写头部导航和页脚 —— 直接复用官网 App 的头部与 PageFooter。
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../seo';
import { PAGE_SEO, COMMON_KEYWORDS } from '../seo/pageSeo';
import { DOMAIN } from '../../config/siteConfig';
import content from './content.json';
import './index.css';

function EnglishText({ children }) {
    return <span className="autumn-en" lang="en">{children}</span>;
}

function Block({ block }) {
    if (block.type === 'h4') {
        return (
            <h4 className="autumn-subhead">
                {block.zh}
                {block.en && <EnglishText>{block.en}</EnglishText>}
            </h4>
        );
    }
    if (block.type === 'ul') {
        return (
            <ul className="autumn-list">
                {block.items.map((item, i) => (
                    <li key={i}>
                        {item.zh}
                        {item.en && <EnglishText>{item.en}</EnglishText>}
                    </li>
                ))}
            </ul>
        );
    }
    return (
        <>
            {block.zh && <p className="autumn-zh">{block.zh}</p>}
            {block.en && <p className="autumn-p-en" lang="en">{block.en}</p>}
        </>
    );
}

export default function Autumn2026() {
    const { t } = useTranslation();
    const seo = PAGE_SEO.autumn;

    return (
        <>
            <SEO
                pageKey="autumn"
                title={seo.title}
                description={seo.description}
                keywords={COMMON_KEYWORDS}
                slug={seo.slug}
                image={`${DOMAIN}${content.pages[0].src}`}
                type="article"
            />
            <div className="autumn-page">
                <div className="autumn-container">
                    {/* ---- 标题区（文案取自 PDF 第 1 页） ---- */}
                    <header className="autumn-hero">
                        <p className="autumn-hero__eyebrow">Xiaomai Automation · Autumn 2026</p>
                        <h1 className="autumn-hero__title">{content.titleZh}</h1>
                        <p className="autumn-hero__title-en" lang="en">{content.titleEn}</p>
                        <p className="autumn-hero__model">
                            {content.modelZh}
                            <EnglishText>{content.modelEn}</EnglishText>
                        </p>
                        <div className="autumn-hero__actions">
                            <a className="autumn-btn autumn-btn--gold" href={content.pdf} download>
                                {t('autumn_download_pdf')}
                            </a>
                            <a className="autumn-btn" href="#autumn-fulltext">
                                {t('autumn_read_text')}
                            </a>
                        </div>
                    </header>

                    {/* ---- PDF 版面（14 页） ---- */}
                    <section className="autumn-pages">
                        <div className="autumn-section-head">
                            <p className="autumn-section-head__eyebrow">Full Spread · 14 Pages</p>
                            <h2>{t('autumn_pages_title')}</h2>
                            <p>{t('autumn_pages_desc')}</p>
                        </div>
                        {content.pages.map((page) => (
                            <figure className="autumn-page-card" key={page.n}>
                                <a href={page.src} target="_blank" rel="noopener noreferrer">
                                    <img
                                        src={page.src}
                                        alt={page.alt}
                                        width="1240"
                                        height="1754"
                                        loading={page.n === 1 ? 'eager' : 'lazy'}
                                        decoding="async"
                                    />
                                </a>
                                <figcaption>
                                    <b>P{String(page.n).padStart(2, '0')}</b>
                                    {page.caption}
                                </figcaption>
                            </figure>
                        ))}
                    </section>

                    {/* ---- 文字版（与 PDF 内容一致，便于检索与复制） ---- */}
                    <section className="autumn-fulltext" id="autumn-fulltext">
                        <div className="autumn-section-head">
                            <h2>{t('autumn_text_title')}</h2>
                        </div>
                        {content.sections.map((section) => (
                            <article className="autumn-block" key={section.id}>
                                <h3>
                                    {section.headingZh}
                                    {section.headingEn && <EnglishText>{section.headingEn}</EnglishText>}
                                </h3>
                                {section.blocks.map((block, i) => (
                                    <Block block={block} key={i} />
                                ))}
                                {section.id === 's9' && content.keyFigures.length > 0 && (
                                    <div className="autumn-figures">
                                        {content.keyFigures.map((figure) => (
                                            <div className="autumn-figure" key={figure.num}>
                                                <div className="autumn-figure__num">{figure.num}</div>
                                                <div className="autumn-figure__label">
                                                    {figure.zh}
                                                    <EnglishText>{figure.en}</EnglishText>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </article>
                        ))}

                        {/* ---- 核心规格速览 ---- */}
                        <article className="autumn-block">
                            <h3>
                                {content.spec.caption}
                                <EnglishText>{content.spec.captionEn}</EnglishText>
                            </h3>
                            <table className="autumn-spec">
                                <tbody>
                                    {content.spec.rows.map((row) => (
                                        <tr key={row.label}>
                                            <th scope="row">
                                                {row.label}
                                                <small>{row.labelEn}</small>
                                            </th>
                                            <td>{row.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </article>
                    </section>
                </div>
            </div>
        </>
    );
}
